import { apiService } from './api';

// Metadata interfaces
export interface ImageMetadata {
  width: number;
  height: number;
  format: string;
  channels: number;
  space?: string;
  hasAlpha: boolean;
  orientation?: number;
  density?: number;
  isAnimated?: boolean;
  pages?: number;
  aspectRatio: number;
}

export interface AudioMetadata {
  duration: number;
  bitrate: number;
  sampleRate: number;
  channels: number;
  channelMode: string;
  codec?: string;
  container?: string;
  title?: string;
  artist?: string;
  album?: string;
}

export interface VideoMetadata {
  duration: number;
  bitrate: number;
  codec?: string;
  container?: string;
  width?: number;
  height?: number;
  fps?: number;
}

export interface FontMetadata {
  format: string;
  size: number;
  family?: string;
  style?: string;
  weight?: string;
  version?: string;
  glyphCount?: number;
}

export interface ModelMetadata {
  format: string;
  size: number;
  vertices?: number;
  faces?: number;
  normals?: number;
  textureCoords?: number;
}

export interface AssetMetadata {
  type: 'image' | 'audio' | 'video' | 'font' | '3d';
  image?: ImageMetadata;
  audio?: AudioMetadata;
  video?: VideoMetadata;
  font?: FontMetadata;
  model?: ModelMetadata;
  error?: string;
}

export interface Asset {
  id: string;
  filename: string;
  type: string;
  size: string;
  uses: number;
  date: string;
  category?: string;
  url?: string;
  isArchived?: boolean;
  uploadProgress?: number;
  metadata?: AssetMetadata | null;
}

/**
 * AssetService
 * Frontend service for managing editor assets
 */
class AssetService {
  /**
   * Upload asset file
   */
  async uploadAsset(
    file: File, 
    projectId: string,
    onProgress?: (progress: number) => void
  ): Promise<Asset> {
    if (!projectId) {
      throw new Error('Project ID is required');
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    
    const response = await apiService.post('/editor/assets/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentComplete = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentComplete);
        }
      },
    });
    
    return response.data.asset;
  }

  /**
   * List assets with optional filters
   */
  async listAssets(
    projectId: string, 
    category?: string,
    includeArchived = false
  ): Promise<Asset[]> {
    if (!projectId) {
      throw new Error('Project ID is required');
    }
    
    const params: any = { projectId };
    if (category && category !== 'all') params.category = category;
    if (includeArchived) params.includeArchived = 'true';
    
    const response = await apiService.get('/editor/assets', { params });
    return response.data.assets;
  }

  /**
   * Rename asset
   */
  async renameAsset(assetId: string, filename: string): Promise<void> {
    await apiService.put(`/editor/assets/${assetId}/rename`, { filename });
  }

  /**
   * Archive multiple assets
   */
  async archiveAssets(assetIds: string[]): Promise<void> {
    await apiService.put('/editor/assets/archive', { assetIds });
  }

  /**
   * Restore multiple assets from archive
   */
  async restoreAssets(assetIds: string[]): Promise<void> {
    await apiService.put('/editor/assets/restore', { assetIds });
  }

  /**
   * Delete asset permanently
   */
  async deleteAsset(assetId: string): Promise<void> {
    await apiService.delete(`/editor/assets/${assetId}`);
  }

  /**
   * Delete multiple assets permanently
   */
  async deleteAssets(assetIds: string[]): Promise<void> {
    // Delete assets in parallel
    await Promise.all(assetIds.map(id => this.deleteAsset(id)));
  }

  /**
   * Calculate usage counts from code
   * Scans code for {{asset:UUID}} patterns
   */
  calculateUsageFromCode(code: string): Map<string, number> {
    const usageMap = new Map<string, number>();
    
    if (!code) return usageMap;
    
    // Match {{asset:UUID}} patterns
    const assetRegex = /\{\{asset:([a-f0-9-]{36})\}\}/gi;
    const matches = code.matchAll(assetRegex);
    
    for (const match of matches) {
      const assetId = match[1];
      usageMap.set(assetId, (usageMap.get(assetId) || 0) + 1);
    }
    
    return usageMap;
  }
}

export default new AssetService();

